export default (file, queueUploadSize, allowsVideo, audioTypes) => ({
  duration: file.duration ?? "--",
  fileName: file.fileName,
  progressNumber: 0,
  progressText: "",
  fileValidation: "",
  dropping: false,
  shouldQueue: false,
  uploadedFileName: null,
  allowsVideo: allowsVideo,
  audioTypes: audioTypes,
  init() {
    this.$watch("allowsVideo", (value) => {
      if (value) {
        this.discardFile();
      }
    });
  },
  async validateFile(event) {
    const file = event.files[0];
    this.$refs.fileInput.value = "";
    if (!file) return;

    this.fileName = file.name;
    this.duration = "--";
    this.updateFileValidation("pending", "Validating file...");
    this.shouldQueue = false;
    this.progressNumber = 0;
    this.progressText = "";
    this.uploadedFileName = "";

    const allowedTypes = this.audioTypes.join(", ");
    const extensionRegex = /(\.[a-z1-9]+)$/i;
    // If the file name ends in a .{extension} pattern, get the extension name, otherwise, assign empty string
    const fileExtension = extensionRegex.test(this.fileName.toLowerCase())
      ? this.fileName.toLowerCase().match(extensionRegex)[0]
      : "";

    if (!fileExtension) {
      this.updateFileValidation(
        "error",
        `No file extension detected. The file should have one of the accepted formats: ${allowedTypes}.`
      );
    } else if (!allowedTypes.includes(fileExtension.split(".").pop())) {
      if (this.$wire.get("episodeAudio")) this.removeUploadedFile();

      if (file.type.includes("video") && !this.allowsVideo) {
        this.updateFileValidation("error");
        this.$wire.call("addNewError", "unsupportedVideo");
      } else {
        this.updateFileValidation(
          "error",
          `Accepted formats are ${allowedTypes}. Please select a new file or remove it.`
        );
      }
      this.$wire.call("addNewError", "episodeAudio");

      return;
    } else if (file.size > 4294967296) {
      // Limit is 4GB
      setTimeout(() => {
        if (this.$wire.get("episodeAudio")) this.removeUploadedFile();
        this.updateFileValidation(
          "error",
          "File is larger than 4GB. Please upload a different one."
        );
        this.$wire.call("addNewError", "episodeAudio");
        return;
      }, 800);
    } else {
      setTimeout(() => {
        // Check that file duration exists before moving forward with upload
        this.getFileDuration(file)
          .then((val) => {
            this.$wire.set("fileDuration", this.duration);
            this.$wire.set("episodeAudioSize", file.size);
            this.$wire.set("episodeAudioType", file.type);

            const waitForStart = setTimeout(() => {
              this.updateFileValidation(
                "error",
                "Something went wrong, we were unable to access the upload server. Try disabling browser extensions. If you continue to have problems, contact support."
              );
            }, 15000);

            this.$wire.upload(
              "episodeAudio",
              file,
              (this.finishCallback = (uploadedFilename) => {
                clearTimeout(waitForStart);
                this.uploadedFileName = uploadedFilename;

                this.progressText = "Completed";
                this.updateFileValidation("success");

                this.$wire.call("errorBag").then((errors) => {
                  if (errors.episodeAudio && errors.episodeAudio.length > 0) {
                    // In case there was backend validation error, just pass error status to update UI color
                    // The message will already be printed in the UI through $errors->first()
                    this.updateFileValidation("error");
                  }
                });
              }),
              (this.errorCallback = () => {
                clearTimeout(waitForStart);
                this.updateFileValidation(
                  "error",
                  "Something went wrong, try uploading again. If you continue to have problems, contact support."
                );
              }),
              (this.progressCallback = (event) => {
                clearTimeout(waitForStart);
                this.progressNumber = event.detail.progress;
                this.progressText = this.progressNumber + "%";

                this.updateFileValidation("uploading");

                if (this.progressNumber === 100) {
                  this.progressText = "Processing...";
                }

                const fileSizeMB = Math.round(file.size / 1024 ** 2);
                if (fileSizeMB >= queueUploadSize) {
                  this.shouldQueue = true;
                }
              })
            );
          })
          .catch((error) => {
            this.updateFileValidation("error", error);
            this.$wire.call("addNewError", "episodeAudio");
            console.error(error);
          });
      }, 800);
    }
  },
  getFileDuration(file) {
    return new Promise((resolve, reject) => {
      // Reject promise if it takes too much time to file size, should be safe to assume that it doesn't have a file size if not returned in that time
      const timer = setTimeout(
        () =>
          reject(
            "No audio duration detected. Please try again or upload a different file."
          ),
        15000
      );

      const audio = document.createElement("video");
      const audioUrl = URL.createObjectURL(file);
      audio.src = audioUrl;
      audio.preload = "metadata";
      audio.addEventListener("loadedmetadata", (e) => {
        const toTime = (seconds) => {
          const HH = `${Math.floor(seconds / 3600)}`.padStart(2, "0");
          const MM = `${Math.floor(seconds / 60) % 60}`.padStart(2, "0");
          const SS = `${Math.floor(seconds % 60)}`.padStart(2, "0");
          return [HH, MM, SS].join(":");
        };
        this.duration = toTime(e.target.duration);
        clearTimeout(timer);
        resolve(this.duration);
      });
    });
  },
  updateFileValidation(status, message = null) {
    const classes = {
      error: {
        container: "bg-red-50 text-red-600",
        message: "text-red-600 text-sm",
        progress: "bg-red-600",
      },
      success: {
        container: "bg-green-50 text-green-700",
        message: "text-green-700 text-sm",
        progress: "bg-green-600",
      },
      uploading: {
        container: "bg-blue-50 text-blue-700",
        message: "text-blue-700 text-sm",
        progress: "bg-blue-600",
      },
      pending: {
        container: "bg-purple-50 text-purple-700",
        message: "text-purple-700 text-sm",
        progress: "bg-transparent",
      },
    };

    // This is necessary to keep the state of the upload and update the frontend
    switch (status) {
      case "pending":
        this.$wire.set("isUploading", true);
        break;
      case "error":
        this.$wire.set("isUploading", false);
        break;
      case "success":
        this.$wire.set("isUploading", false);
        break;
    }

    this.fileValidation = {
      status: status,
      message: message,
      containerClasses: classes[status].container,
      messageClasses: classes[status].message,
      progressClasses: classes[status].progress,
    };
  },
  removeUploadedFile() {
    this.$wire.removeUpload(
      "episodeAudio",
      this.uploadedFileName,
      (this.finishCallback = (uploadedFilename) => {}),
      (this.errorCallback = () => {})
    );
  },
  discardFile() {
    // Only reset fileName variable if the episode doesn't have a file name associated
    // This is used to conditionally show the "Upload file" or "Replace file" text in uploader button
    if (!file.fileName) this.fileName = "";

    // Remove temporary upload
    if (this.$wire.get("episodeAudio")) this.removeUploadedFile();

    this.$wire.emit("episode.update-ai-features", false);
    this.$wire.resetErrorValidation("episodeAudio");
    this.$wire.resetErrorValidation("unsupportedVideo");
    this.fileValidation = "";
    this.uploadedFileName = "";
    this.shouldQueue = false;
  },
});
